/**
 * Owner: Masa Bayu
 * File: TaxonomyDetailPanel.tsx
 * Date:Time: 2026-09-15 21:14
 * Description: T2 category/collection detail — book cards only, no purchase.
 */

import {
  findTaxonomyNodeBySlug,
  resolveTaxonomySurface,
  type QxreadTaxonomyKind,
} from '@qx/qxread-core';
import { createDemoCatalog } from '../publication/create-demo-catalog';
import { createDemoTaxonomyIndex } from './create-demo-taxonomy';
import { QxBookCard } from './QxBookCard';

export interface TaxonomyDetailPanelProps {
  readonly kind: QxreadTaxonomyKind;
  readonly slug: string;
  readonly onNavigate: (path: string) => void;
}

export function TaxonomyDetailPanel({ kind, slug, onNavigate }: TaxonomyDetailPanelProps) {
  const index = createDemoTaxonomyIndex();
  const node = findTaxonomyNodeBySlug(index, kind, slug);
  const backPath = kind === 'category' ? '/categories' : '/collections';
  const kindLabel = kind === 'category' ? 'Category' : 'Collection';

  if (node === null) {
    return (
      <section className="route-panel" aria-labelledby="route-title">
        <p className="eyebrow">T2 Marketplace · {kindLabel}</p>
        <h2 id="route-title">{kindLabel} not found</h2>
        <p>
          No {kind} matches <code>{slug}</code>. Indexes are presentation-only.
        </p>
        <p>
          <a
            href={`#${backPath}`}
            onClick={(event) => {
              event.preventDefault();
              onNavigate(backPath);
            }}
          >
            Back to {kind === 'category' ? 'categories' : 'collections'}
          </a>
        </p>
      </section>
    );
  }

  const surface = resolveTaxonomySurface(createDemoCatalog(), node);

  return (
    <section className="route-panel" aria-labelledby="route-title">
      <p className="eyebrow">T2 Marketplace · {kindLabel}</p>
      <h2 id="route-title">{node.label}</h2>
      <p>
        {surface.cards.length} title{surface.cards.length === 1 ? '' : 's'} · public metadata only ·
        not a purchase path
      </p>
      {surface.empty ? (
        <p role="status">This {kind} has no marketplace-ready titles yet.</p>
      ) : (
        <ul className="catalog-list" aria-label={`${node.label} titles`}>
          {surface.cards.map((card) => (
            <li key={card.publicationId}>
              <QxBookCard card={card} onNavigate={onNavigate} idPrefix={node.taxonomyId} />
            </li>
          ))}
        </ul>
      )}
      <p>
        <a
          href={`#${backPath}`}
          onClick={(event) => {
            event.preventDefault();
            onNavigate(backPath);
          }}
        >
          Back to {kind === 'category' ? 'categories' : 'collections'}
        </a>
      </p>
    </section>
  );
}
